import Table from '@eduzz/houston-ui/Table';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Icon from '@material-ui/core/Icon';
import Tooltip from '@material-ui/core/Tooltip';
import Alert from 'components/Alert';
import MyIcon from 'components/Icon';
import { enums } from 'helpers';
import moment from 'moment';
import React, { memo, useCallback, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { toast } from 'react-toastify';
import { useWebHookHistoric, useWebHookScreen } from 'v2/components/Pages/Webhook/context';
import { TableActionButton } from 'v2/components/Shared/TableActionButton';
import Truncate from 'v2/components/Shared/TruncateText';
import webhookService from 'v2/services/webhook';

import type { IWebHookListHistoric } from 'v2/interfaces/webhook/historic';

import QuickView from '../QuickView';
import styles from './styles';

const List = memo(() => {
  const classes = styles();

  const { loading } = useWebHookScreen();
  const { listHistoric, pagination, setPagination, updateListHistoric, setQuickView, setWebhookDetails, filtersHistoric } = useWebHookHistoric();

  const [loadingResend, setLoadingResend] = useState<string>('');
  const [copyWebhook, setCopyWebhook] = useState<string>('');

  const onChangePage = (current_page: number) => {
    setPagination({ ...pagination, current_page });
    updateListHistoric({ ...pagination, current_page });
  };

  const onChangeRowsPerPage = (per_page: number) => {
    setPagination({ ...pagination, per_page });
    updateListHistoric({ ...pagination, current_page: 1, per_page });
  };

  const handleClickResend = useCallback(
    async (webhook: IWebHookListHistoric) => {
      const confirm = await Alert.show({
        message: 'Deseja realmente reenviar esse webhook para essa url?',
        title: 'Reenviar webhook',
        confirmation: true,
      });

      if (!confirm) {
        return;
      }

      setLoadingResend(webhook.payloadId);

      webhookService
        .resendURL(webhook)
        .pipe()
        .subscribe(
          () => {
            toast.success('Webhook reenviado com sucesso');
            setLoadingResend('');
          },
          (err) => {
            toast.error(err);
            setLoadingResend('');
          }
        );
    },
    [setLoadingResend]
  );

  const handleClickDetails = useCallback(
    (item: IWebHookListHistoric) => {
      setWebhookDetails(item);
      setQuickView(true);
    },
    [setWebhookDetails, setQuickView]
  );

  const handleClickCopy = useCallback(
    (id: string) => {
      setCopyWebhook(id);

      setTimeout(() => {
        setCopyWebhook('');
      }, 1500);
    },
    [setCopyWebhook]
  );

  return (
    <>
      <Table total={pagination?.total_items || 0} loading={loading}>
        <Table.Header>
          {filtersHistoric.type === 'invoice' && <Table.Column>Fatura/Contrato</Table.Column>}
          <Table.Column>Produto</Table.Column>
          <Table.Column>Comprador</Table.Column>
          <Table.Column>Últ. Envio</Table.Column>
          <Table.Column>URL</Table.Column>
          <Table.Column>Retorno HTTP</Table.Column>
          <Table.Column>Envio</Table.Column>
          <Table.Column></Table.Column>
        </Table.Header>
        <Table.Body>
          {listHistoric.map((item, index) => (
            <Table.Row data={item} index={index} key={item.payloadId}>
              {filtersHistoric.type === 'invoice' && (
                <Table.Cell>
                  {item.type === 'invoice' ? (
                    item.entityId
                  ) : (
                    <div className={classes.invoice}>
                      <div>{item.entityId}</div>

                      <Tooltip placement='top' title='Recorrência'>
                        <div className={classes.icon}>
                          <MyIcon className='icon' name='icon-assinaturas' />
                        </div>
                      </Tooltip>
                    </div>
                  )}

                  <div className={classes.status}>
                    {item.type === 'invoice' ? (
                      <span className={`${classes.invoiceStatus} ${enums.INVOICE_STATUS_CLASSNAME[item.entityStatus]}`}>
                        {enums.INVOICE_STATUS_NAME[item.entityStatus]}
                      </span>
                    ) : (
                      <span className={`${classes.invoiceStatus} ${enums.SUBSCRIPTIONS_STATUS_CLASSNAME[item.entityStatus]}`}>
                        {enums.SUBSCRIPTIONS_STATUS_NAME[item.entityStatus]}
                      </span>
                    )}
                  </div>
                </Table.Cell>
              )}
              <Table.Cell>
                <Truncate count={20} final='...' title={item.product.title}>
                  {item.product.title}
                </Truncate>
                {item.product.id !== -1 && `(${item.product.id})`}
              </Table.Cell>
              <Table.Cell>
                <Truncate count={25} final='...' title={item.customer.name}>
                  {item.customer.name}
                </Truncate>
              </Table.Cell>
              <Table.Cell>
                {item.updated_at ? (
                  <>
                    {moment(item.updated_at).format('DD/MM/YYYY')} <br />
                    {moment(item.updated_at).format('HH:mm')}
                  </>
                ) : (
                  '-'
                )}
              </Table.Cell>
              <Table.Cell>
                <div className={classes.copy}>
                  <Truncate count={25} final='...' title={item.url ? item.url : '-'}>
                    {item.url ? item.url : '-'}
                  </Truncate>

                  {item.url && (
                    <Tooltip title={copyWebhook === item.payloadId ? 'Copiado!' : 'Clique para copiar'} placement='top'>
                      <CopyToClipboard text={item.url} onCopy={() => handleClickCopy(item.payloadId)}>
                        <Icon>content_copy</Icon>
                      </CopyToClipboard>
                    </Tooltip>
                  )}
                </div>
              </Table.Cell>
              <Table.Cell>
                <LastHttpResponse item={item} />
              </Table.Cell>
              <Table.Cell>
                <Button
                  disabled={loadingResend === item.payloadId}
                  variant='text'
                  size='small'
                  startIcon={loadingResend === item.payloadId ? <CircularProgress size={16} /> : <Icon>send</Icon>}
                  onClick={() => handleClickResend(item)}
                >
                  Tentar novamente
                </Button>
              </Table.Cell>

              <Table.Cell align='right'>
                <TableActionButton onClick={() => handleClickDetails(item)} />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
        {!loading && pagination && (
          <Table.Pagination
            page={pagination?.current_page}
            perPage={pagination?.per_page}
            onChangePage={onChangePage}
            onChangePerPage={onChangeRowsPerPage}
            optionsPerPage='10,20,50,100,200'
          />
        )}
      </Table>
      <QuickView />
    </>
  );
});

const LastHttpResponse = ({ item }: { item: IWebHookListHistoric }) => {
  const classes = styles();
  const size = item.sends.length;

  const solveStatusColor = useCallback((status: string): string => {
    const statusInt = Number.parseInt(status);
    if (statusInt >= 200 && statusInt < 300) return 'status-200';
    return 'status-504';
  }, []);

  if (!size) return <>-</>;

  const status = item.sends[size - 1].returnStatus;

  return <span className={`${classes.httpStatus} ${solveStatusColor(status)}`}> {status} </span>;
};

export default List;
